import { BatchStatuses, EbrStatuses, OperationExecutionStatuses } from "@tm-food/api-contract";
import type { MesBatch, MesHold } from "@/modules/mes/api/mesApi";
import { mesOpStatusLabel } from "@/modules/mes/components/mesOpLabels";
import type { SemanticTone } from "@/shared/ui/core/types";

/** Luồng chính mẻ: Chờ cấp phát → Đã cấp phát → Đang sản xuất → Hoàn tất (+ Tạm giữ / Đã hủy). */
const BATCH_STATUS_LABEL: Record<string, string> = {
  Wip: "Đang vận hành trên chuyền (WIP)",
  [BatchStatuses.Planned]: "Chờ cấp phát",
  /** Legacy — cùng nghĩa chưa cấp phát NVL. */
  [BatchStatuses.Released]: "Chờ cấp phát",
  [BatchStatuses.Issued]: "Đã cấp phát",
  [BatchStatuses.InProgress]: "Đang sản xuất",
  [BatchStatuses.Hold]: "Tạm giữ",
  [BatchStatuses.Completed]: "Hoàn tất",
  [BatchStatuses.Cancelled]: "Đã hủy",
};

/** Filter list — bỏ Released (gộp vào Kế hoạch), bổ sung Wip cho điều hướng từ BI. */
export const BATCH_STATUS_FILTER_VALUES = [
  "Wip",
  BatchStatuses.Planned,
  BatchStatuses.Issued,
  BatchStatuses.InProgress,
  BatchStatuses.Hold,
  BatchStatuses.Completed,
  BatchStatuses.Cancelled,
] as const;

export function batchStatusLabel(status: string): string {
  return BATCH_STATUS_LABEL[status] ?? status;
}

/**
 * Màu trạng thái mẻ — mỗi bước một tone, tránh trùng Issued/InProgress (info)
 * và Hold/Cancelled (danger).
 *
 * Chờ cấp phát → warning · Đã cấp phát → accent · Đang SX → info
 * · Tạm giữ → hold · Hoàn tất → success · Đã hủy → danger
 */
export function batchStatusTone(status: string): SemanticTone {
  switch (status) {
    case BatchStatuses.Planned:
    case BatchStatuses.Released:
      return "warning";
    case BatchStatuses.Issued:
      return "success";
    case BatchStatuses.InProgress:
      return "info";
    case BatchStatuses.Hold:
      return "danger";
    case BatchStatuses.Completed:
      return "success";
    case BatchStatuses.Cancelled:
      return "danger";
    default:
      return "draft";
  }
}

export function batchStatusFilterOptions(
  values: readonly string[] = BATCH_STATUS_FILTER_VALUES,
) {
  return values.map((value) => ({ value, label: batchStatusLabel(value) }));
}

/** Chưa cấp phát NVL — chưa được bắt đầu sản xuất. */
export function batchAwaitsMaterialIssue(status: string): boolean {
  return status === BatchStatuses.Planned || status === BatchStatuses.Released;
}

/** Đã cấp phát (hoặc đang chạy) — được Start công đoạn. */
export function batchAllowsOperationStart(status: string): boolean {
  return status === BatchStatuses.Issued || status === BatchStatuses.InProgress;
}

export function ebrStatusLabel(status: string): string {
  if (status === EbrStatuses.Open) return "Đang mở";
  if (status === EbrStatuses.Closed) return "Đã đóng";
  return status;
}

export function batchOperationStatusLabel(status: string): string {
  if (status === OperationExecutionStatuses.Blocked) return "Tạm giữ";
  return mesOpStatusLabel(status);
}

export function batchPackDisplay(batch: Pick<MesBatch, "packFormatLabel">): string {
  return batch.packFormatLabel?.trim() || "—";
}

export function batchLineDisplay(batch: Pick<MesBatch, "lineName">): string {
  return batch.lineName?.trim() || "Chưa gán";
}

export function batchShiftDisplay(batch: Pick<MesBatch, "shiftName">): string {
  return batch.shiftName?.trim() || "—";
}

export function formatQty(value: number): string {
  return value.toLocaleString("vi-VN", { maximumFractionDigits: 2 });
}

export type SummaryMetric = {
  id: string;
  label: string;
  value: string;
  hint?: string;
  tone?: SemanticTone;
};

export function buildBatchSummaryMetrics(
  batch: MesBatch,
  openHoldCount: number,
): SummaryMetric[] {
  const pct =
    batch.operationCount > 0
      ? Math.round((batch.completedOps / batch.operationCount) * 1000) / 10
      : null;
  return [
    {
      id: "status",
      label: "Trạng thái",
      value: batchStatusLabel(batch.status),
      tone: batchStatusTone(batch.status),
    },
    {
      id: "ops",
      label: "Công đoạn",
      value: `${formatQty(batch.completedOps)}/${formatQty(batch.operationCount)}`,
      hint: pct != null ? `${formatQty(pct)}%` : undefined,
      tone: "primary",
    },
    {
      id: "ebr",
      label: "Hồ sơ mẻ",
      value: ebrStatusLabel(batch.ebrStatus),
      tone: batch.ebrStatus === EbrStatuses.Open ? "warning" : "neutral",
    },
    {
      id: "holds",
      label: "Lệnh giữ",
      value: formatQty(openHoldCount),
      tone: openHoldCount > 0 ? "danger" : "success",
    },
    {
      id: "line",
      label: "Dây chuyền",
      value: batchLineDisplay(batch),
      tone: batch.lineName?.trim() ? "neutral" : "warning",
    },
  ];
}

export type AttentionActionId = "field" | "holds" | "mo" | "ebr" | "materials";

export type AttentionItem = {
  id: string;
  severity: "danger" | "warning" | "info";
  title: string;
  detail?: string;
  badgeLabel?: string;
  meta?: string;
  actionLabel?: string;
  actionId?: AttentionActionId;
};

export function buildBatchAttentionItems(input: {
  batch: MesBatch;
  openHolds: MesHold[];
  canExec: boolean;
}): AttentionItem[] {
  const { batch, openHolds, canExec } = input;
  const items: AttentionItem[] = [];

  if (openHolds.length > 0) {
    items.push({
      id: "open-holds",
      severity: "danger",
      badgeLabel: "LỆNH GIỮ",
      meta: `${openHolds.length} lệnh`,
      title: `${openHolds.length} lệnh giữ đang mở`,
      detail: openHolds
        .slice(0, 2)
        .map((h) => h.reason)
        .join(" · "),
      actionLabel: "Xem lệnh giữ",
      actionId: "holds",
    });
  }

  if (batch.status === BatchStatuses.Hold) {
    items.push({
      id: "batch-hold",
      severity: "warning",
      badgeLabel: "TẠM DỪNG",
      title: "Mẻ đang bị giữ — Chưa được chạy tiếp",
      detail:
        "Mẻ sản xuất đang chịu lệnh kiểm soát tạm giữ. Cần gỡ lệnh giữ trước khi tiếp tục các công đoạn tiếp theo.",
    });
  }

  if (batchAwaitsMaterialIssue(batch.status)) {
    items.push({
      id: "issue-materials",
      severity: "warning",
      badgeLabel: "NVL",
      title: "Chưa cấp phát NVL — chưa được bắt đầu sản xuất",
      actionLabel: "Cấp phát NVL",
      actionId: "materials",
    });
  }

  if (batch.ebrStatus === EbrStatuses.Open) {
    items.push({
      id: "ebr",
      severity: "info",
      badgeLabel: "HỒ SƠ MẺ",
      meta: "Đang mở",
      title: "Hồ sơ mẻ đang mở",
      detail:
        "Nhật ký mẻ điện tử đang ghi nhận các thông số vận hành và kiểm tra chất lượng theo thời gian thực.",
      actionLabel: "Mở hồ sơ",
      actionId: "ebr",
    });
  } else if (batch.ebrStatus === EbrStatuses.Closed) {
    items.push({
      id: "ebr-closed",
      severity: "info",
      badgeLabel: "HỒ SƠ MẺ",
      meta: "Đã đóng",
      title: "Hồ sơ mẻ đã đóng",
      detail:
        "Toàn bộ dữ liệu sản xuất đã được khóa và lưu trữ bất biến phục vụ truy xuất nguồn gốc.",
      actionLabel: "Xem hồ sơ",
      actionId: "ebr",
    });
  }

  const nextOp = batch.operations.find(
    (o) => o.status !== OperationExecutionStatuses.Completed && o.status !== OperationExecutionStatuses.Cancelled,
  );
  if (
    nextOp &&
    canExec &&
    batchAllowsOperationStart(batch.status) &&
    batch.status !== BatchStatuses.Hold &&
    batch.status !== BatchStatuses.Cancelled
  ) {
    items.push({
      id: "next-op",
      severity: "info",
      badgeLabel: "TIẾP THEO",
      title: `Công đoạn tiếp: ${nextOp.opName?.trim() || "Chưa đặt tên"}`,
      detail: "Công đoạn sẵn sàng để vận hành viên tiếp nhận vật tư, thông số và bắt đầu thực thi.",
      actionLabel: "Thực thi công đoạn",
      actionId: "field",
    });
  }

  return items;
}
